// src/features/rrhh/empleados/components/EmpleadosCargaMasivaPreview.jsx
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

function isDuplicateError(error) {
  const text = String(error || "").toLowerCase();
  return text.includes("ya existe") || text.includes("duplicado");
}

function getRowFlags(item) {
  const errors = item.errors || [];
  const hasDuplicates = errors.some(isDuplicateError);
  const hasNonDuplicateErrors = errors.some((err) => !isDuplicateError(err));

  return {
    isValid: item.valid,
    hasDuplicates,
    hasNonDuplicateErrors,
  };
}

function getCardStyles(item) {
  const { isValid, hasDuplicates, hasNonDuplicateErrors } = getRowFlags(item);

  if (isValid) {
    return {
      bg: "rgba(227, 247, 236, 1)",
      border: "1px solid rgba(71, 167, 106, 0.35)",
    };
  }

  // Si tiene errores "reales", priorizamos visualmente rojo
  if (hasNonDuplicateErrors) {
    return {
      bg: "rgba(255, 232, 232, 1)",
      border: "1px solid rgba(214, 84, 84, 0.35)",
    };
  }

  // Si solo tiene repetidos
  if (hasDuplicates) {
    return {
      bg: "rgba(255, 247, 224, 1)",
      border: "1px solid rgba(222, 170, 35, 0.35)",
    };
  }

  return {
    bg: "rgba(255, 232, 232, 1)",
    border: "1px solid rgba(214, 84, 84, 0.35)",
  };
}

function StatusChips({ item }) {
  const { isValid, hasDuplicates, hasNonDuplicateErrors } = getRowFlags(item);

  if (isValid) {
    return (
      <Chip
        label="Válida"
        sx={{
          bgcolor: "rgba(71, 167, 106, 0.16)",
          color: "rgba(23, 104, 54, 1)",
          fontWeight: 700,
        }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
      {hasDuplicates && (
        <Chip
          label="Dato repetido"
          sx={{
            bgcolor: "rgba(255, 193, 7, 0.16)",
            color: "rgba(140, 98, 0, 1)",
            fontWeight: 700,
          }}
        />
      )}

      {hasNonDuplicateErrors && (
        <Chip
          label="Inválida"
          sx={{
            bgcolor: "rgba(214, 84, 84, 0.16)",
            color: "rgba(165, 33, 33, 1)",
            fontWeight: 700,
          }}
        />
      )}
    </Stack>
  );
}

function PreviewCard({ item }) {
  const data = item.raw || {};
  const cardStyles = getCardStyles(item);

  return (
    <Box
      sx={{
        borderRadius: "18px",
        p: 1.6,
        bgcolor: cardStyles.bg,
        border: cardStyles.border,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ justifyContent: "space-between", alignItems: { xs: "flex-start", md: "center" } }}
        >
          <Stack spacing={0.25}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
              Fila {item.rowNumber} · {data.full_name || "-"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "rgba(2, 8, 23, 0.72)" }}>
              {data.email || "-"} · DNI: {data.dni || "-"}
            </Typography>
          </Stack>

          <StatusChips item={item} />
        </Stack>

        <Typography sx={{ fontSize: 12.5, color: "rgba(2, 8, 23, 0.82)" }}>
          <b>Sucursales:</b> {data.branch_names || "-"} &nbsp;&nbsp;|&nbsp;&nbsp;
          <b>Áreas:</b> {data.area_names || "-"} &nbsp;&nbsp;|&nbsp;&nbsp;
          <b>Roles:</b> {data.role_names || "-"}
        </Typography>

        {!item.valid && (
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            {(item.errors || []).map((err) => {
              const duplicate = isDuplicateError(err);

              return (
                <Chip
                  key={err}
                  label={err}
                  size="small"
                  sx={{
                    bgcolor: duplicate
                      ? "rgba(255, 193, 7, 0.14)"
                      : "rgba(214, 84, 84, 0.12)",
                    color: duplicate
                      ? "rgba(140, 98, 0, 1)"
                      : "rgba(130, 22, 22, 1)",
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default function EmpleadosCargaMasivaPreview({
  loading,
  fileError,
  rows = [],
  totalRows = 0,
  validRows = 0,
  invalidRows = 0,
}) {
  return (
    <Box
      sx={{
        bgcolor: "rgba(247, 247, 248, 1)",
        borderRadius: "20px",
        p: 2,
        boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
      }}
    >
      <Stack spacing={1.4}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
          Previsualización de filas
        </Typography>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.75)" }}>
              Analizando archivo...
            </Typography>
          </Stack>
        )}

        {!loading && fileError && (
          <Alert severity="error" sx={{ borderRadius: "14px" }}>
            {fileError}
          </Alert>
        )}

        {!loading && !fileError && (
          <>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label={`Total: ${totalRows}`}
                sx={{ bgcolor: "rgba(216, 220, 237, 1)", fontWeight: 700 }}
              />
              <Chip
                label={`Válidas: ${validRows}`}
                sx={{
                  bgcolor: "rgba(71, 167, 106, 0.16)",
                  color: "rgba(23, 104, 54, 1)",
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`Inválidas: ${invalidRows}`}
                sx={{
                  bgcolor: "rgba(214, 84, 84, 0.16)",
                  color: "rgba(165, 33, 33, 1)",
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
              <Stack spacing={1}>
                {rows.map((item, index) => (
                  <PreviewCard
                    key={`${item.rowNumber}-${index}`}
                    item={item}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}